import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  Divider,
  Modal,
  Checkbox,
  createTheme,
  ThemeProvider,
  InputLabel,
  FormControl,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormHelperText,
  Grid,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import "@fontsource/manrope";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { ReactComponent as GenerateButton } from "../../images/generateButton.svg";
import {
  getAllSprints,
  getFilteredBugs,
  getProjectVersions,
} from "../../services/webServices";
import { useNavigate } from "react-router-dom";

const theme = createTheme({
  typography: {
    fontFamily: "Manrope, Arial, sans-serif",
  },
});

const BugReportFiltersPage = ({
  setOpenFiltersPage,
  setParent,
  sprintsList,
  setSprintsList,
  versionsList,
  setVersionsList,
  statusList
}) => {
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [selectedFilters, setSelectedFilters] = useState({
    sprintIds: [],
    status: [],
    releaseVersion: [],
    priority: []
  });
  const [accordionExpanded, setAccordionExpanded] = useState(true);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleCloseModal = () => {
    // setIsModalOpen(false);
  };

  const handleFilterChange = (filterName) => (event) => {
    const value = event.target.value;
    const updatedFilters = {
      ...selectedFilters,
      [filterName]: typeof value === "string" ? value.split(",") : value,
    };

    // Check if any filter is applied
    const anyFilterApplied = Object.values(updatedFilters).some((filter) =>
      Array.isArray(filter) ? filter.length > 0 : filter !== ""
    );

    setSelectedFilters(updatedFilters);
    setIsFilterApplied(anyFilterApplied);
  };

  //Handle Sprints Filter
  const handleFilterSprints = (filterName) => (event) => {
    const value = event.target.value;

    const updatedFilters = {
      ...selectedFilters,
      [filterName]: value, // Update with selected sprint IDs
    };

    // Check if any filter is applied
    const anyFilterApplied = Object.values(updatedFilters).some((filter) =>
      Array.isArray(filter) ? filter.length > 0 : filter !== ""
    );

    setSelectedFilters(updatedFilters);
    setIsFilterApplied(anyFilterApplied);
  };

  //Hanlde Versions Filter
  const handleVersionFilter = (filterName) => (event) => {
    const value = event.target.value;

    const updatedFilters = {
      ...selectedFilters,
      [filterName]: value, // Update with selected sprint IDs
    };

    // Check if any filter is applied
    const anyFilterApplied = Object.values(updatedFilters).some((filter) =>
      Array.isArray(filter) ? filter.length > 0 : filter !== ""
    );

    setSelectedFilters(updatedFilters);
    setIsFilterApplied(anyFilterApplied);
  };

  //Handle Status Filter
  const handleStatusFilter = (filterName) => (event) => {
    const value = event.target.value;

    const updatedFilters = {
      ...selectedFilters,
      [filterName]: value, // Update with selected sprint IDs
    };

    // Check if any filter is applied
    const anyFilterApplied = Object.values(updatedFilters).some((filter) =>
      Array.isArray(filter) ? filter.length > 0 : filter !== ""
    );

    setSelectedFilters(updatedFilters);
    setIsFilterApplied(anyFilterApplied);
  };

  //Handle Prioriy Filter
  const handlePriorityFilter = (filterName) => (event) => {
    const value = event.target.value;

    const updatedFilters = {
      ...selectedFilters,
      [filterName]: value, // Update with selected sprint IDs
    };

    // Check if any filter is applied
    const anyFilterApplied = Object.values(updatedFilters).some((filter) =>
      Array.isArray(filter) ? filter.length > 0 : filter !== ""
    );

    setSelectedFilters(updatedFilters);
    setIsFilterApplied(anyFilterApplied);
  };

  const handleAccordionChange = () => {
    setAccordionExpanded(!accordionExpanded);
  };

  const handleResetFilters = () => {
    const originalFilters = {
      sprintIds: [],
      status: [],
      releaseVersion: [],
      priority: []
    };

    setSelectedFilters(originalFilters);
    setIsFilterApplied(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="import-bug-description-title"
        aria-describedby="import-bug-description-instructions"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "600px",
            height: "550px", // Fixed modal height
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            p: 3,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            outline: "none",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
            <IconButton
              size="small"
              sx={{ color: "black", "& svg": { fontSize: "0.9rem" } }}
              onClick={() => setParent(null)}
            >
              <ArrowBackIosNewIcon />
            </IconButton>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "normal",
                color: "black",
                fontSize: "1.0rem",
              }}
            >
              Import Bug Description
            </Typography>
          </Box>

          {/* Instructions */}
          <Typography
            id="import-bug-description-instructions"
            variant="subtitle2"
            sx={{ textAlign: "left", color: "text.secondary", mb: 4 }}
          >
            Enter details or select parameters to generate results
          </Typography>

          <TextField
            label="Defect ID"
            placeholder="Enter issue id, defect id, key"
            variant="outlined"
            size="small"
            sx={{
              width: "39%",
              marginBottom: 2,
              ".MuiInputBase-root": {
                borderRadius: 1,
              },
            }}
          />

          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            {/* Accordion for Filters */}
            <Accordion
              expanded={accordionExpanded}
              onChange={handleAccordionChange}
              sx={{
                flexGrow: 1,
                boxShadow: "none",
                border: "none",
                "&:before": {
                  display: "none",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="filter-content"
                id="filter-header"
                sx={{
                  flexDirection: "row-reverse",
                  "& .MuiAccordionSummary-expandIconWrapper": {
                    marginRight: "auto",
                  },
                  mb: -1,
                  ml: -2,
                }}
              >
                <Typography>More Filters</Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  height: "200px", // Reserve space for accordion content
                  overflow: "hidden",
                  display: accordionExpanded ? "block" : "none", // Show or hide content without resizing
                  ml: -2,
                }}
              >
                <Grid container spacing={3}>
                  {/* //SPRINT FILTER */}
                  <Grid item xs={4}>
                    <FormControl
                      fullWidth
                      variant="outlined"
                      size="small"
                      sx={{
                        minWidth: 180,
                        marginBottom: 0, // Optional: reduce bottom margin if any
                      }}
                    >
                      <InputLabel id="sprint-filter-label">Sprint</InputLabel>
                      <Select
                        labelId="sprint-filter-label"
                        value={selectedFilters.sprintIds}
                        onChange={handleFilterSprints("sprintIds")}
                        label="Sprint"
                        multiple
                        renderValue={(selected) =>
                          selected
                            .map(
                              (id) =>
                                sprintsList.find((sprint) => sprint.id === id)
                                  ?.name
                            )
                            .join(", ")
                        }
                        sx={{
                          paddingTop: 0,
                          paddingBottom: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 200,
                              overflow: "auto",
                            },
                          },
                        }}
                      >
                        {sprintsList.length <= 0 ? (
                          <MenuItem>No Sprints Available</MenuItem>
                        ) : (
                          sprintsList.map((sprint) => (
                            <MenuItem key={sprint.id} value={sprint.id}>
                              <Checkbox
                                checked={selectedFilters.sprintIds.includes(
                                  sprint.id
                                )}
                              />
                              {sprint.name}
                            </MenuItem>
                          ))
                        )}
                      </Select>

                      {selectedFilters.sprintIds === "" && (
                        <FormHelperText>Choose a Sprint</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Status Filter */}
                  <Grid item xs={4}>
                    <FormControl
                      fullWidth
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 180, marginBottom: 0 }}
                    >
                      <InputLabel id="status-filter-label">Status</InputLabel>
                      <Select
                        labelId="status-filter-label"
                        value={selectedFilters.status}
                        onChange={handleStatusFilter("status")}
                        label="Status"
                        multiple
                        renderValue={(selected) => selected.join(", ")}
                        sx={{
                          paddingTop: 0,
                          paddingBottom: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <MenuItem value="To Do">
                          <Checkbox
                            checked={selectedFilters.status.includes("To Do")}
                          />
                          To Do
                        </MenuItem>
                        <MenuItem value="In Progress">
                          <Checkbox
                            checked={selectedFilters.status.includes(
                              "In Progress"
                            )}
                          />
                          In Progress
                        </MenuItem>
                        <MenuItem value="Done">
                          <Checkbox
                            checked={selectedFilters.status.includes("Done")}
                          />
                          Done
                        </MenuItem>
                        <MenuItem value="Backlog">
                          <Checkbox
                            checked={selectedFilters.status.includes("Backlog")}
                          />
                          Backlog
                        </MenuItem>
                        <MenuItem value="Testing">
                          <Checkbox
                            checked={selectedFilters.status.includes("Testing")}
                          />
                          Testing
                        </MenuItem>
                        <MenuItem value="Blocked">
                          <Checkbox
                            checked={selectedFilters.status.includes("Blocked")}
                          />
                          Blocked
                        </MenuItem>
                        <MenuItem value="Ready for QA">
                          <Checkbox
                            checked={selectedFilters.status.includes("Ready for QA")}
                          />
                          Ready for QA
                        </MenuItem>
                        <MenuItem value="Verified">
                          <Checkbox
                            checked={selectedFilters.status.includes("Verified")}
                          />
                          Verified
                        </MenuItem>
                      </Select>
                      {selectedFilters.status === "" && (
                        <FormHelperText>Choose a status</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={4}>
                    <FormControl
                      fullWidth
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 180, marginBottom: 0 }}
                    >
                      <InputLabel id="release_version-filter-label">
                        Release Version
                      </InputLabel>
                      <Select
                        labelId="release_version-filter-label"
                        value={selectedFilters.releaseVersion}
                        onChange={handleVersionFilter("releaseVersion")}
                        label="Release_Version"
                        multiple
                        renderValue={(selected) =>
                          selected
                            .map(
                              (id) =>
                                versionsList.find(
                                  (version) => version.id === id
                                )?.name
                            )
                            .join(", ")
                        }
                        sx={{
                          paddingTop: 0,
                          paddingBottom: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 200,
                              overflow: "auto",
                            },
                          },
                        }}
                      >
                        {versionsList.length === 0 ? (
                          <MenuItem>No Released Versions</MenuItem>
                        ) : (
                          versionsList.map((version) => (
                            <MenuItem key={version.id} value={version.id}>
                              <Checkbox
                                checked={selectedFilters.releaseVersion.includes(
                                  version.id
                                )}
                              />
                              {version.name}
                            </MenuItem>
                          ))
                        )}


                      </Select>
                      {selectedFilters.releaseVersion === "" && (
                        <FormHelperText>
                          Choose a release_version
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Priority Filter */}
                  <Grid item xs={4}>
                    <FormControl
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 180, marginBottom: 0 }}
                    >
                      <InputLabel id="priority-label">Priority</InputLabel>
                      <Select
                        labelId="priority-label"
                        value={selectedFilters.priority}
                        onChange={handlePriorityFilter("priority")}
                        label="Priority"
                        multiple
                        renderValue={(selected) => selected.join(", ")}
                        sx={{
                          paddingTop: 0,
                          paddingBottom: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <MenuItem value="Low">
                          <Checkbox
                            checked={selectedFilters.priority.includes("Low")}
                          />
                          Low
                        </MenuItem>
                        <MenuItem value="Medium">
                          <Checkbox
                            checked={selectedFilters.priority.includes(
                              "Medium"
                            )}
                          />
                          Medium
                        </MenuItem>
                        <MenuItem value="High">
                          <Checkbox
                            checked={selectedFilters.priority.includes("High")}
                          />
                          High
                        </MenuItem>
                      </Select>
                      {selectedFilters.priority === "" && (
                        <FormHelperText>Choose a Priority</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Box>

          {errorMessage && <Box>{errorMessage}</Box>}

          {/* Divider */}
          <Divider sx={{ marginY: 2 }} />

          {/* Buttons */}
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <a
              style={{
                display: "flex",
                alignItems: "center",
                cursor: isFilterApplied ? "pointer" : "default",
                textDecoration: "none",
                color: isFilterApplied ? "#49A3FF" : "#9e9e9e", // Changed to blue when enabled
                fontWeight: 400,
                pointerEvents: isFilterApplied ? "auto" : "none",
              }}
              onClick={isFilterApplied ? handleResetFilters : undefined}
            >
              <RestartAltIcon
                style={{
                  marginRight: 4,
                  color: isFilterApplied ? "#49A3FF" : "#9e9e9e", // Changed to blue when enabled
                }}
              />
              <Typography
                variant="body2"
                style={{
                  color: isFilterApplied ? "#49A3FF" : "#9e9e9e", // Changed to blue when enabled
                  fontWeight: 400,
                }}
              >
                Reset Filter
              </Typography>
            </a>
            <Button
              variant="contained"
              size="small"
              // startIcon={<GenerateButton />}
              sx={{
                backgroundColor: isFilterApplied ? "#49A3FF" : "#f0f0f0",
                color: isFilterApplied ? "#ffffff" : "#9e9e9e",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: isFilterApplied ? "#49A3FF" : "#f0f0f0",
                },
              }}
              disabled={!isFilterApplied}
              onClick={() => { }}
            >
              Search
            </Button>
          </Box>
        </Box>
      </Modal>
    </ThemeProvider>
  );
};

export default BugReportFiltersPage;
